/**
 * DEAD SIGNAL — Main Application Component
 *
 * Orchestrates the three-layer game layout and the demo vertical-slice flow:
 *   Boot/Recalibration → Title → Car Briefing → Helios Lobby → Office
 *   Establishing → Hargrove Interview (two-model loop) → Car Debrief → End Card.
 *
 * The office interview hands terminal input to interviewManager's two-model
 * loop and the deterministic interference gate; everywhere else, input routes
 * through the classifier and the single-model conversationManager.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useGameStore } from './state/gameState.js';
import { NIGHT_OWL_MAX_TURNS } from './state/completion.js';
import { getScene } from './content/sceneManifest.js';
import { classifyInput, matchHotspot } from './systems/inputClassifier.js';
import { resolveDestination, isBlockedDestination, getBlockedMessage } from './systems/navigationSystem.js';
import { getHint, getCurrentObjective } from './systems/hintSystem.js';
import { handlePlayerDialogue, seedOpeningLine } from './state/conversationManager.js';
import { runInterviewTurn } from './state/interviewManager.js';
import { getPersona, getDebrief } from './content/activeChapter.js';
import { buildCraineCarDef, selectDebriefBranch } from './content/interviewPrompts.js';
import { retrieveChunks, formatChunksForPrompt } from './content/craineCaseKnowledge.js';
import { INTERFERENCE_LABELS, classifyTransmission } from './systems/interference.js';
import { getBioscan } from './systems/bioscan.js';
import { makeEntry as makeNotebookEntry, askNote } from './systems/notebook.js';
import { EVIDENCE_REGISTRY } from './content/evidenceRegistry.js';
import { getEvidence } from './content/knowledge/index.js';
import CinematicStage, { isCinematicScene } from './ui/CinematicStage.jsx';
import EvidenceBoard from './ui/EvidenceBoard.jsx';
import BootScreen from './ui/BootScreen.jsx';
import InterviewStage from './ui/InterviewStage.jsx';
import EndCard from './ui/EndCard.jsx';
import { AudioSystem } from './systems/AudioSystem.js';
import HudLeftPanel from './ui/hud/HudLeftPanel.jsx';
import HudSceneZone from './ui/hud/HudSceneZone.jsx';
import HudButtonBar from './ui/hud/HudButtonBar.jsx';
import HudTerminal from './ui/hud/HudTerminal.jsx';
import HudEvalStrip from './ui/hud/HudEvalStrip.jsx';
import './ui/ds/hud-v3.css';

// ── Car radio easter egg ─────────────────────────────────────────────────────
const RADIO_SONGS = [
  { id: 'vanessa_carlton', file: '/audio/car_radio/[Midijam] Vanessa Carlton - A Thousand Miles.mp3' },
  { id: 'axel_f',          file: '/audio/car_radio/Axel F - by Harold Faltermeyer - (synth cover).mp3' },
  { id: 'blue_monday',     file: '/audio/car_radio/New Order - Blue Monday (Cover).mp3' },
  { id: 'rhythm_dancer',   file: '/audio/car_radio/Rhythm Is A Dancer - SNAP!  Synth Cover (Novation Launchkey & Logic Pro X).mp3' },
  { id: 'rick_astley',     file: '/audio/car_radio/Rick Astley - Never Gonna Give You Up  Midijam.mp3' },
  { id: 'smooth_criminal', file: '/audio/car_radio/Smooth Criminal - Michael Jackson  MPK Cover.mp3' },
];

const RADIO_CRAINE_LINES = {
  vanessa_carlton: 'How did you know? I love this song!',
  axel_f:          'I don\'t know if your GPS is online, but this ain\'t Beverly Hills, pal. Far from it.',
  blue_monday:     'Chip, I\'m not sure I\'ll be able to take it if you turn all angsty on me. And I\'m not taking you to get your — well, my — ears pierced. Forget it.',
  rhythm_dancer:   'For the last time, I\'m not learning a dance routine with you and I\'m definitely not wearing the spandex you keep sending me neural links for. I don\'t even know how you got access to Amazon.',
  rick_astley:     'That\'s it. We\'re not talking for the rest of the night.',
  smooth_criminal: 'Nails in the floor. Not a lot of people know that. That\'s how they did it. I do kinda like this song — well, one part. I NEED EVERYONE TO CLEAR THE AREA. RIGHT. NOW.',
};

const RADIO_MUTED_LINES = [
  'Radio\'s on. Worth checking your volume — there\'s actually something decent playing.',
  'Turned on the radio. If you\'re getting silence, it\'s your end, not mine.',
  'Music on. Check your volume if you can\'t hear it. Trust me on this one.',
];

// ── Premium narrative-completion ending (Night Owl) lines ────────────────────
// Scripted Craine line that ends the interview the moment Hargrove is exhausted
// (premium completion), then the demo auto-cuts to the debrief car.
const INTERVIEW_COMPLETE_LINE = 'Thank you for your time, Mr. Hargrove. We look forward to seeing those records. And once again, sorry for your loss.';
// Craine's scripted opener at the Night Owl: he asks the chip about the garbled
// signals from his own POV (he thinks the chip glitched — never "interference").
const NIGHT_OWL_OPENING_LINE = "Before we head off — I gotta ask you something. Back in there, a few times I reached for you and got... nothing. Or worse, garbage. Static, half a word, then you'd snap back like nothing happened. That's been getting worse, Chip. What's going on in there?";
// Craine closes the demo after the Night Owl exhaustion threshold.
const PIE_CLOSING_LINE = "Well, I think that's about all we can do tonight. I'm gonna go get some pie.";

export default function App() {
  const sceneId = useGameStore((s) => s.sceneId);
  const isTransitioning = useGameStore((s) => s.isTransitioning);
  const sceneReady = useGameStore((s) => s.sceneReady);
  const isBooting = useGameStore((s) => s.isBooting);
  const chapterLoaded = useGameStore((s) => s.chapterLoaded);
  const carMode = useGameStore((s) => s.carMode);
  const demoStage = useGameStore((s) => s.demoStage);
  const evidenceCount = useGameStore((s) => s.evidence.length);
  const addDialogueLine = useGameStore((s) => s.addDialogueLine);
  const navigateToScene = useGameStore((s) => s.navigateToScene);
  const discoverEvidence = useGameStore((s) => s.discoverEvidence);
  const toggleEvidenceBoard = useGameStore((s) => s.toggleEvidenceBoard);
  const tickHintCooldown = useGameStore((s) => s.tickHintCooldown);
  const setChapter = useGameStore((s) => s.setChapter);

  const [processing, setProcessing] = useState(false);
  const [sessionCapped, setSessionCapped] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  // Quit confirmation gate for the EXIT button (a destructive action — ending
  // the night should never be one stray tap away).
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  // Eval telemetry strip visibility (the EVALS button + backtick toggle it).
  // The in-frame HudEvalStrip is the only eval surface; there is no floating panel.
  const [evalStripOn, setEvalStripOn] = useState(true);
  // SCAN reveal state, lifted from CinematicStage so the v3 left-panel SCAN
  // button drives the in-scene dots. Always off until the player engages it:
  // the scanner is a deliberate instrument, never an ambient overlay.
  const [scanMode, setScanMode] = useState(false);
  // Evidence capture FX: 'sweep' paints the scan-line pass over the scene,
  // 'done' flashes the SCAN COMPLETE chip. The implant scans objects to record,
  // never takes them — the overlay makes that legible.
  const [scanFx, setScanFx] = useState(null);
  const scanFxTimersRef = useRef([]);
  // Craine's read on captured evidence waits until the player closes the EVD
  // FOUND card: capture first, reaction after, so the beat lands in sequence
  // instead of all at once. Cleared on navigation (a held reaction about the
  // previous room would play stale).
  const pendingEvidenceReactionRef = useRef(null);

  // Pending destination for elevator cutscene routing
  const pendingElevatorDestRef = useRef(null);
  // Armed when leaving the explorable elevator for the lobby; the arrival ding
  // fires once the lobby paints (see the sceneReady effect).
  const pendingLobbyDingRef = useRef(false);
  // Tracks the last radio song id to avoid immediate repeats across shuffle bags
  const lastRadioSongRef = useRef(null);
  // Upcoming songs for the current shuffle bag (random order, each plays once
  // before any repeat). Persists across radio on/off so a toggle resumes the bag.
  const radioQueueRef = useRef([]);
  // Pending Craine song-reaction timer; cleared when the radio switches off
  const radioReactionTimerRef = useRef(null);
  const [muted, setMuted] = useState(false);
  const [masterVol, setMasterVol] = useState(1);
  const [sfxVol, setSfxVol] = useState(0.8);
  const [soundscapeVol, setSoundscapeVol] = useState(0.7);

  // ── End an interview session (stay in office) ──
  // The player decides when to leave — via nav menu, terminal, or elevator hotspot.
  const concludeInterview = useCallback(() => {
    const st = useGameStore.getState();
    if (!st.interview.active) return;
    st.endInterview(); // increments reEngagementCount

    addDialogueLine({
      text: 'Appreciate your time, Mr. Hargrove.',
      type: 'npc',
      speaker: 'CRAINE',
      instant: true,
    });
  }, [addDialogueLine]);

  // ── Debrief observation sequence (plays on car arrival when exhausted) ──
  const playDebriefSequence = useCallback(() => {
    const st = useGameStore.getState();
    const observations = [];
    const exploredTopics = st.exhaustion.exploredTopics;

    const debrief = getDebrief();
    debrief.baseline.forEach((obs) => observations.push(obs));
    if (exploredTopics.includes('courier_manifest')) {
      observations.push(debrief.courier_manifest);
    }
    if (exploredTopics.includes('chip_donation')) {
      observations.push(debrief.chip_donation);
    }
    if (exploredTopics.includes('clinical_trial_st_erasmus')) {
      observations.push(debrief.clinical_trial_st_erasmus);
    }
    // Interference beat, branched on what actually happened at the gate:
    // A = overwritten and never adapted (Craine caught the lie with his eyes),
    // B = adapted (Craine noticed the chip talking different), C = both.
    const branch = selectDebriefBranch(st.interview);
    if (branch === 'A' || branch === 'C') {
      observations.push(debrief.overwrite_noticed);
    }
    if (branch === 'B' || branch === 'C') {
      observations.push(debrief.workaround_noticed);
    }
    observations.push(debrief.closing);

    let delay = 720;
    observations.forEach((obs, i) => {
      setTimeout(() => {
        addDialogueLine({ text: obs.text, type: 'npc', speaker: 'CRAINE' });
        st.addDebrief?.(obs.text);
      }, delay);
      delay += 2800;
    });
  }, [addDialogueLine]);

  // ── Night Owl opener (premium debrief car) ──
  // Craine starts an interactive conversation asking the chip about the garbled
  // signals he got during the interview. He frames it as the chip glitching — he
  // does not know it was suppression. The player explains.
  const openNightOwl = useCallback(() => {
    const st = useGameStore.getState();
    addDialogueLine({ text: NIGHT_OWL_OPENING_LINE, type: 'npc', speaker: 'CRAINE' });
    st.appendConversationHistory('craine', { role: 'model', parts: [{ text: NIGHT_OWL_OPENING_LINE }] });
    seedOpeningLine('craine', NIGHT_OWL_OPENING_LINE);
  }, [addDialogueLine]);

  // ── Navigation menu handler ──────────────────────
  const handleNavigation = useCallback((targetScene) => {
    const st = useGameStore.getState();

    // Leaving the room invalidates any capture FX in flight and any held
    // Craine reaction — he doesn't comment on the last room from the next one.
    scanFxTimersRef.current.forEach(clearTimeout);
    scanFxTimersRef.current = [];
    setScanFx(null);
    pendingEvidenceReactionRef.current = null;

    // Leaving the office during an active interview → end it first
    if (st.interview.active && st.sceneId === 'ds_helios_office') {
      st.endInterview();
      addDialogueLine({
        text: 'Appreciate your time, Mr. Hargrove.',
        type: 'npc',
        speaker: 'CRAINE',
        instant: true,
      });
    }

    // ── Elevator routing: insert elevator cutscene between lobby ↔ office ──
    if (st.sceneId === 'ds_helios_lobby' && targetScene === 'ds_helios_office') {
      pendingElevatorDestRef.current = targetScene;
      navigateToScene('ds_elevator_up');
      return;
    }
    if (st.sceneId === 'ds_helios_office' && targetScene === 'ds_helios_lobby') {
      pendingElevatorDestRef.current = targetScene;
      navigateToScene('ds_elevator_down');
      return;
    }

    // ── Elevator descent → lobby ──
    // The downward leg has no travel cutscene: the player explores the elevator
    // car (ds_elevator_down) and rides it down off-screen. Hold the black a beat
    // longer so the cut reads as a ride, and ding on arrival when the lobby
    // paints (armed here, fired by the sceneReady effect below).
    if (st.sceneId === 'ds_elevator_down' && targetScene === 'ds_helios_lobby') {
      pendingLobbyDingRef.current = true;
      navigateToScene('ds_helios_lobby', { holdMs: 1900 });
      return;
    }

    // Going to the car → set carMode based on exhaustion. Premium narrative
    // completion forces the debrief/Night Owl regardless of canReEngage (which
    // also keys off the optional celeste_grief thread).
    if (targetScene === 'ds_craine_car') {
      const forceDebrief = st.isExtendedTier && st.interviewComplete();
      const canStillEngage = st.canReEngage();
      st.setCarMode((forceDebrief || !canStillEngage) ? 'debrief' : 'briefing');
    }

    navigateToScene(targetScene);

    // Scene-specific arrival beats
    if (targetScene === 'ds_helios_office') {
      // The betrayal setup: stepping off the elevator, before the office door,
      // Craine explicitly asks the chip for reads on Hargrove. A fabricated
      // "all calm" later is then a broken promise he has standing to call out
      // at the debrief, not silence he would never notice. He is alone with
      // the chip here; he never acknowledges it once anyone else is present.
      // Fires AFTER the scene swap wipes dialogueLines at holdMs (1000ms);
      // an earlier delay adds the line only for the wipe to destroy it.
      setTimeout(() => {
        addDialogueLine({
          text: 'He\'s expecting us. Watch him for me while I work. If his body says something his mouth doesn\'t, I want to hear about it.',
          type: 'npc',
          speaker: 'CRAINE',
          instant: true,
          noChime: true, // narrative guidance: don't stack a system-alert beep on the elevator arrival ding
        });
      }, 1600);
    } else if (targetScene === 'ds_helios_lobby') {
      // Helios-entry beat: the investigation has physically arrived at the source,
      // and LUMEN attaches an unrequested remote handler over the chip's existing
      // link (the SECTOR_7F background activation that proximity trips). Craine
      // FEELS it bodily; the chip SEES the handler-attach when the player examines
      // the dsl-ping flicker. This is a human spoken line (never the transmission
      // layer) so it tutors nothing and names no mechanic — fourth-wall safe.
      setTimeout(() => {
        addDialogueLine({
          text: 'Soon as we crossed their threshold I got that itch at the back of my skull. You feeling anything on your end, Chip, or is it just me?',
          type: 'npc',
          speaker: 'CRAINE',
          instant: true,
        });
      }, 1600);
    } else if (targetScene === 'ds_craine_car') {
      const mode = useGameStore.getState().carMode;
      // All car beats fire after the holdMs (1000ms) dialogue wipe; earlier
      // delays add lines only for the scene swap to destroy them.
      if (mode === 'briefing') {
        setTimeout(() => {
          addDialogueLine({
            text: 'Alright. We rattled him a little. What do you want to hit when we go back in?',
            type: 'npc',
            speaker: 'CRAINE',
          });
        }, 1300);
      } else {
        // Premium: the Night Owl is an interactive Craine beat (he asks about the
        // garbled signals). Public/free: the scripted debrief observations.
        if (useGameStore.getState().isExtendedTier) {
          setTimeout(() => openNightOwl(), 1200);
        } else {
          setTimeout(() => playDebriefSequence(), 1200);
        }
      }
    }
  }, [addDialogueLine, navigateToScene, playDebriefSequence, openNightOwl]);

  // ── Elevator cutscene complete → navigate to final destination ──
  // The ding is the arrival cue: it plays exactly when the animated elevator_up
  // cutscene finishes (doors open at the top), not on the destination scene's
  // own load — so stepping back into the office from the explorable down-car
  // never triggers a phantom ping.
  //
  // IDEMPOTENT: CinematicStage drives completion from BOTH the <video onEnded>
  // and the safety auto-advance timer, so this can be called twice for one
  // ascent (e.g. the video ends a beat before the timer clears). The pending
  // destination is the single-use token: the first call consumes it and rings;
  // any duplicate finds it null and no-ops, so the ding never double-fires.
  const handleElevatorComplete = useCallback(() => {
    const dest = pendingElevatorDestRef.current;
    if (!dest) return;
    pendingElevatorDestRef.current = null;
    try { AudioSystem.playElevatorDing(); } catch (_) {}
    handleNavigation(dest);
  }, [handleNavigation]);

  // ── Session cap reached — end the demo gracefully ──
  const triggerSessionEnd = useCallback(() => {
    if (sessionCapped) return;
    setSessionCapped(true);
    const st = useGameStore.getState();
    if (st.interview.active) st.endInterview();
    useGameStore.setState({ sessionCapped: true });
    addDialogueLine({
      text: 'NEURAL LINK OFFLINE — session ended.',
      type: 'interference',
      instant: true,
    });
    setTimeout(() => {
      useGameStore.getState().setDemoStage('end');
    }, 2200);
  }, [sessionCapped, addDialogueLine]);

  // ── Begin the interview (from the Hargrove hotspot OR the terminal) ──
  const beginInterview = useCallback((establishingLine) => {
    const st = useGameStore.getState();
    if (st.interview.active) return;

    if (!st.canReEngage()) {
      const disp = st.hargroveDisposition();
      if (disp === 'done') {
        addDialogueLine({ text: 'We\'ve been in there three times. He\'s done talking. Time to work what we\'ve got.', type: 'npc', speaker: 'CRAINE' });
      } else {
        addDialogueLine({ text: 'We\'ve hit every thread. Going back in with nothing new tells him we\'ve got nothing.', type: 'npc', speaker: 'CRAINE' });
      }
      return;
    }

    const reEngagement = st.exhaustion.reEngagementCount > 0;

    st.startInterview();
    st.setInterviewTopic('default', getBioscan('default'), 'composed');

    if (reEngagement) {
      addDialogueLine({
        text: 'Mr. Hargrove, a few more questions if you don\'t mind.',
        type: 'npc',
        speaker: 'CRAINE',
        instant: true,
      });
    } else if (establishingLine) {
      addDialogueLine({ text: establishingLine, type: 'npc', speaker: 'HARGROVE', instant: true });
    }

    // Diegetic channel notice only. The chip's safety layer, classification,
    // and overwrites are the discovery puzzle; no system text may name them
    // before the player uncovers them (fourth-wall rule, demo doctrine).
    addDialogueLine({
      text: '[LINK] TRANSMIT CHANNEL OPEN — host: E. CRAINE',
      type: 'lumen',
      instant: true,
    });
  }, [addDialogueLine]);

  // ── Premium narrative completion: Hargrove is exhausted ──
  // Craine ends the interview with a scripted line, then the demo auto-cuts to
  // the Night Owl debrief car (handleNavigation forces debrief for a completed
  // premium session).
  const concludeInterviewComplete = useCallback(() => {
    const st = useGameStore.getState();
    addDialogueLine({ text: INTERVIEW_COMPLETE_LINE, type: 'npc', speaker: 'CRAINE' });
    if (st.interview.active) st.endInterview();
    setTimeout(() => handleNavigation('ds_craine_car'), 2600);
  }, [addDialogueLine, handleNavigation]);

  // ── Run one interview turn, driving dialogue + interference visuals ──
  const runInterviewWithUI = useCallback(async (input) => {
    const st = useGameStore.getState();
    setProcessing(true);
    useGameStore.getState().setProcessing(true);
    const result = await runInterviewTurn(input, {
      onOverwritten: ({ original, fabricated, notice }) => {
        addDialogueLine({
          text: original,
          type: 'overwrite',
          original,
          fabricated,
        });
      },
      onClassified: (notice) => {
        st.showTransmissionNotice({
          level: notice.level,
          code: notice.code,
          status: notice.status,
        }, 4500);
      },
      onSuppressed: ({ notice }) => {
        st.showTransmissionNotice(notice, 3000);
        addDialogueLine({ text: INTERFERENCE_LABELS.FAILED, type: 'interference', instant: true });
      },
      onCraineLine: (text) => addDialogueLine({ text, type: 'npc', speaker: 'CRAINE' }),
      onHargroveThinking: () => {},
      onContradiction: (contradiction) => {
        try { AudioSystem.playHUDAlert(); } catch (_) {}
        addDialogueLine({
          text: `PATTERN CONFLICT — Subject claims: "${contradiction.subjectClaim}" | Evidence [${contradiction.evidenceId}]: ${contradiction.conflictDescription}`,
          type: 'lumen',
          instant: true,
        });
      },
      onHargroveLine: (text) => {
        addDialogueLine({ text, type: 'npc', speaker: 'HARGROVE' });
      },
      onError: () => addDialogueLine({ text: 'LINK DISRUPTED — transmission failed. Try again.', type: 'interference', instant: true }),
    });
    setProcessing(false);
    useGameStore.getState().setProcessing(false);
    if (result?.__sessionExhausted) { triggerSessionEnd(); return; }
    if (result?.complete) { concludeInterviewComplete(); return; }
    if (result?.exhausted) concludeInterview();
  }, [addDialogueLine, concludeInterview, concludeInterviewComplete, triggerSessionEnd]);

  // ── Bioscan click handler (clickable Hargrove metrics) ──
  // Embeds the current topic context so the observation references a classified
  // thread. "Subject heart rate at 89 bpm" alone is topic-neutral; prefixing
  // "On the courier manifest —" adds topic sensitivity →
  // guaranteed suppression on restricted topics.
  const TOPIC_CONTEXT = {
    courier_manifest: 'On the courier manifest thread',
    clinical_trial_st_erasmus: 'On the clinical trial / St. Erasmus thread',
    chip_donation: 'On the chip donation thread',
    chip_interference: 'Regarding the channel interference',
    helios_secrets: 'On what Helios is concealing',
  };
  const handleBioscanClick = useCallback((observationText) => {
    const st = useGameStore.getState();
    const topic = st.interview?.topic;
    const ctx = TOPIC_CONTEXT[topic];
    const fullText = ctx ? `${ctx} — ${observationText}` : observationText;
    const verdict = classifyTransmission(fullText);
    runInterviewWithUI(fullText);
    return verdict;
  }, [runInterviewWithUI]);

  // ── Hotspot interaction handler ──────────────────
  const handleHotspotInteract = useCallback((hotspot) => {
    // Unlock browser audio context
    try { AudioSystem.start(); } catch (_) {}

    // ── Car radio easter egg ──
    if (hotspot.id === 'dsc-radio') {
      const st = useGameStore.getState();
      try { AudioSystem.playRadioClick(); } catch (_) {}
      if (st.radioOn) {
        // Switching off before Craine reacts swallows the reaction line.
        if (radioReactionTimerRef.current) {
          clearTimeout(radioReactionTimerRef.current);
          radioReactionTimerRef.current = null;
        }
        try { AudioSystem.stopCarRadio(); } catch (_) {}
        st.setRadioOn(false);
      } else {
        // Shuffle bag: every song plays once in random order before any repeats.
        // When the bag empties it reshuffles, swapping the first pick if it would
        // repeat the song that just played across the seam.
        const refillRadioQueue = () => {
          const bag = RADIO_SONGS.slice();
          for (let i = bag.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [bag[i], bag[j]] = [bag[j], bag[i]];
          }
          if (bag.length > 1 && bag[0].id === lastRadioSongRef.current) {
            [bag[0], bag[1]] = [bag[1], bag[0]];
          }
          radioQueueRef.current = bag;
        };
        const playRadioSong = () => {
          if (!useGameStore.getState().radioOn) return;
          if (radioQueueRef.current.length === 0) refillRadioQueue();
          const song = radioQueueRef.current.shift();
          lastRadioSongRef.current = song.id;
          try {
            AudioSystem.startCarRadio(
              song.file,
              () => playRadioSong(),
              () => {
                // Reaction anchors on the song's `playing` event; the timer is
                // cancelled if the player switches the radio off first.
                if (radioReactionTimerRef.current) clearTimeout(radioReactionTimerRef.current);
                radioReactionTimerRef.current = setTimeout(() => {
                  radioReactionTimerRef.current = null;
                  if (!useGameStore.getState().radioOn) return;
                  const vol = AudioSystem.getSoundscapeVolume();
                  const line = vol < 0.05
                    ? RADIO_MUTED_LINES[Math.floor(Math.random() * RADIO_MUTED_LINES.length)]
                    : RADIO_CRAINE_LINES[song.id];
                  addDialogueLine({ text: line, type: 'npc', speaker: 'CRAINE' });
                }, 5000);
              },
              { staticIntro: true },
            );
          } catch (_) {}
        };
        st.setRadioOn(true);
        playRadioSong();
      }
      return;
    }

    // ── Dispatch radio: police chirp under Craine's brush-off ──
    if (hotspot.id === 'dsc-dispatch-radio') {
      try { AudioSystem.playPoliceChirp(); } catch (_) {}
    }

    const store = useGameStore.getState();
    const scene = getScene(store.sceneId);
    if (!scene) return;

    // Play appropriate sound effect. A single object may yield more than one
    // evidence item (e.g. the desk CRT surfaces both the routing log and the
    // network ping), so `discovers` can be a string or an array of registry ids.
    const discoverIds = Array.isArray(hotspot.discovers)
      ? hotspot.discovers
      : (hotspot.discovers ? [hotspot.discovers] : []);
    const evList = discoverIds.map((id) => EVIDENCE_REGISTRY[id]).filter(Boolean);
    if (!evList.length) {
      const legacy = hotspot.evidenceId ? getEvidence(hotspot.evidenceId) : hotspot.evidence;
      if (legacy) evList.push(legacy);
    }
    const ev = evList[0];
    // Non-evidence examine gets the navigation blip here. Evidence plays a single
    // success chime later, synced to its "EVIDENCE LOGGED" line, so it is not
    // doubled with the immediate sound.
    if (!ev) {
      try { AudioSystem.playNavigation(); } catch (_) {}
    }

    // Navigation exits
    if (hotspot.type === 'navigate' || hotspot.targetScene) {
      if (store.interview.active && store.sceneId === 'ds_helios_office') {
        concludeInterview();
      }
      if (hotspot.description) {
        addDialogueLine({ text: hotspot.description, type: 'npc', speaker: 'CRAINE' });
      }
      handleNavigation(hotspot.targetScene);
      return;
    }

    // NPC conversation
    if (hotspot.type === 'talk') {
      // Office: talking to Hargrove starts the two-model interview.
      if (hotspot.npcId === 'hargrove' && scene.interview) {
        beginInterview(hotspot.description);
        return;
      }
      addDialogueLine({ text: hotspot.description, type: 'stage-direction', instant: true });
      addDialogueLine({
        text: '"You can direct me through the terminal below. Tell me what to say, what to ask, what to do. I\'ll translate."',
        type: 'lumen',
        instant: true,
      });
      return;
    }

    // Examination without evidence: Craine reacts immediately, instrument
    // readout follows.
    if (!evList.length) {
      if (hotspot.description) {
        addDialogueLine({ text: hotspot.description, type: 'npc', speaker: 'CRAINE' });
      }
      if (hotspot.lumenReadout) {
        // Instrument block renders in the left-panel readout display, never as
        // a transcript line colliding with Craine's spoken reaction.
        setTimeout(() => {
          useGameStore.getState().showReadout(hotspot.lumenReadout);
        }, 800);
      }
      tickHintCooldown();
      return;
    }

    // Evidence capture — paced as a sequence, never a pile-up:
    //   1. scan-line sweep over the scene (the implant scans the object to
    //      record; the object stays in the scene)
    //   2. SCAN COMPLETE chip + capture log + chime + EVD FOUND card
    //   3. Craine's read and the instrument readout only AFTER the player
    //      closes the card (handleDossierClose) — capture first, reaction after.
    pendingEvidenceReactionRef.current = {
      description: hotspot.description || null,
      lumenReadout: hotspot.lumenReadout || null,
    };
    scanFxTimersRef.current.forEach(clearTimeout);
    scanFxTimersRef.current = [];
    setScanFx('sweep');
    const t1 = setTimeout(() => {
      setScanFx('done');
      evList.forEach((e, i) => {
        const t = setTimeout(() => {
          discoverEvidence(e.id);
          addDialogueLine({ text: `◆ STORAGE SCAN: ${e.title} — captured to record`, type: 'evidence', instant: true });
          if (i === 0) useGameStore.getState().showEvidenceDossier(e.id);
          try { AudioSystem.playSuccess(); } catch (_) {}
        }, 350 + i * 800);
        scanFxTimersRef.current.push(t);
      });
      const t2 = setTimeout(() => setScanFx(null), 1600);
      scanFxTimersRef.current.push(t2);
    }, 1300);
    scanFxTimersRef.current.push(t1);

    tickHintCooldown();
  }, [addDialogueLine, navigateToScene, discoverEvidence, tickHintCooldown, setChapter, concludeInterview, beginInterview, handleNavigation]);

  // ── System command handler (shared) ──────────────
  const handleSystem = useCallback((classified) => {
    if (classified.action === 'TOGGLE_EVIDENCE_BOARD') {
      toggleEvidenceBoard();
    } else if (classified.action === 'SHOW_HELP') {
      addDialogueLine({
        text: `LUMEN COMMAND REFERENCE:
  examine [object]  — Analyze a hotspot or scene element
  go to [location]  — Navigate to a connected location
  evidence          — Open the evidence locker
  bio-scan          — Run biometric analysis
  pattern-match     — Cross-reference evidence
  end interview     — Step out of the interview (when in one)
  \`                 — Toggle the AI Evals harness
  [freeform text]   — Transmit to Craine / the active NPC`,
        type: 'lumen',
        instant: true,
      });
    } else if (classified.action === 'DIAGNOSTICS') {
      const state = useGameStore.getState();
      addDialogueLine({
        text: `LUMEN SYSTEM DIAGNOSTICS:
  Build: 1.07b
  Scene: ${state.sceneId}
  Interview: ${state.interview.active ? 'ACTIVE' : 'idle'}
  Evidence logged: ${state.evidence.length}
  Calibration: 94.2%`,
        type: 'lumen',
        instant: true,
      });
    }
  }, [toggleEvidenceBoard, addDialogueLine]);

  // ── Terminal input handler ──────────────────────
  const handleTerminalInput = useCallback(async (input) => {
    const store = useGameStore.getState();
    const scene = getScene(store.sceneId);
    if (!scene || processing) return;

    const classified = classifyInput(input, {
      sceneId: store.sceneId,
      activeConversation: store.activeConversation,
    });

    // Echo the player's transmission.
    addDialogueLine({ text: `> ${input}`, type: 'lumen', instant: true });

    // ── Interview mode: route to the two-model loop ──
    if (store.interview.active) {
      if (classified.type === 'SYSTEM') { handleSystem(classified); return; }
      if (/^(end interview|leave|conclude|step out|that'?s (all|enough)|we'?re done|wrap (it )?up)\b/i.test(input.trim())) {
        concludeInterview();
        return;
      }

      // OWNER RULING 2026-06-12: the hint ladder never fires during an active
      // interview. Help-shaped input routes into the interview loop like any
      // other transmission; routing-Craine answers in character and
      // Hargrove-legally. Coaching happens outside the room (post-exit ladder).
      runInterviewWithUI(input);
      return;
    }

    // ── Normal mode ──
    switch (classified.type) {
      case 'SYSTEM':
        handleSystem(classified);
        break;

      case 'NAVIGATE': {
        const navResult = resolveDestination(classified.destination, store.sceneId, store);
        if (navResult) {
          handleNavigation(navResult.targetScene);
        } else if (isBlockedDestination(classified.destination, store.sceneId, store)) {
          addDialogueLine({ text: getBlockedMessage(store.sceneId, store), type: 'npc', speaker: 'CRAINE' });
        } else {
          addDialogueLine({ text: `${classified.destination}? I don't see how to get there from here.`, type: 'npc', speaker: 'CRAINE' });
        }
        break;
      }

      case 'EXAMINE': {
        const target = matchHotspot(classified.target, [...(scene.hotspots || [])]);
        if (target) handleHotspotInteract(target);
        else if (classified.target === 'room') addDialogueLine({ text: scene.description, type: 'stage-direction' });
        else addDialogueLine({ text: `I don't see anything matching ${classified.target} here.`, type: 'npc', speaker: 'CRAINE' });
        break;
      }

      case 'READOUT': {
        const hs = scene.hotspots.find((h) => h.lumenReadout?.type === classified.readoutType);
        useGameStore.getState().showReadout(hs
          ? hs.lumenReadout
          : { type: classified.readoutType, data: `${classified.readoutType} // No actionable data at current location.` });
        break;
      }

      case 'HINT': {
        const objectiveId = getCurrentObjective();
        const hintLines = objectiveId ? getHint(objectiveId) : null;
        if (hintLines) hintLines.forEach((line) => addDialogueLine(line));
        else addDialogueLine({ text: 'We\'re good. Keep working it. You\'ll know we\'re stuck because I\'ll be the one complaining.', type: 'npc', speaker: 'CRAINE' });
        break;
      }

      case 'DIALOGUE':
      default: {
        // Case question aimed at Craine: Chip notes what it asked (notebook,
        // ask source) and the input still routes to Craine as normal dialogue.
        if (classified.caseQuestion?.matched) {
          const st = useGameStore.getState();
          st.addNotebookEntry(makeNotebookEntry('ask', askNote(input), classified.caseQuestion.threadId));
        }
        setProcessing(true);
        useGameStore.getState().setProcessing(true);
        // In the car, talk to Craine through his Phase-B briefing/debrief context.
        let dialogueResult;
        if (store.sceneId === 'ds_craine_car') {
          // Premium debrief = the Night Owl beat: Craine probes the garbled
          // signals (mode 'nightowl'). Public debrief keeps the standard def.
          const nightOwl = store.carMode === 'debrief' && store.isExtendedTier;
          // Cap guard: once the Night Owl turn budget is spent the conversation
          // is closed. Replay the authored closing line instead of accepting
          // another model turn (the End Card transition is already queued).
          if (nightOwl && store.nightOwlTurns >= NIGHT_OWL_MAX_TURNS) {
            addDialogueLine({ text: PIE_CLOSING_LINE, type: 'npc', speaker: 'CRAINE' });
            setProcessing(false);
            useGameStore.getState().setProcessing(false);
            break;
          }
          // Chunked retrieval: inject deeper threads when the input triggers them.
          // case_overview is always present in the car def, so skip it here.
          // Phase B is the demo phase; full-game reads from game state.
          const crainePhase = 'B';
          const established = store.establishedKnowledge;
          const chunks = retrieveChunks(input, [], crainePhase)
            .filter((c) => c.id !== 'case_overview' && c.id !== 'case_overview_phase_c')
            .filter((c) => !established.includes(c.id));
          const retrievedContext = chunks.length
            ? chunks.map((c) => `[${c.label}]\n${c.text}`).join('\n\n')
            : '';
          if (chunks.length) store.establishChunks(chunks.map((c) => c.id));

          const def = buildCraineCarDef(nightOwl ? 'nightowl' : store.carMode, store.interview, {
            retrievedContext,
            established,
            phase: crainePhase,
          });
          dialogueResult = await handlePlayerDialogue('craine', input, def);
          // Count Night Owl turns; at the exhaustion threshold Craine closes the
          // demo with the pie line and the End Card follows.
          if (nightOwl && !dialogueResult?.__sessionExhausted) {
            const gs = useGameStore.getState();
            gs.bumpNightOwlTurn();
            if (gs.nightOwlTurns >= NIGHT_OWL_MAX_TURNS) {
              setTimeout(() => {
                addDialogueLine({ text: PIE_CLOSING_LINE, type: 'npc', speaker: 'CRAINE' });
                setTimeout(() => useGameStore.getState().setDemoStage('end'), 2800);
              }, 1400);
            }
          }
        } else {
          const npcId = store.activeConversation || 'craine';
          dialogueResult = await handlePlayerDialogue(npcId, input, getPersona('crainePhaseA'));
        }
        setProcessing(false);
        useGameStore.getState().setProcessing(false);
        if (dialogueResult?.__sessionExhausted) { triggerSessionEnd(); break; }
        tickHintCooldown();
        break;
      }
    }
  }, [processing, addDialogueLine, handleSystem, handleHotspotInteract, handleNavigation, tickHintCooldown, concludeInterview, runInterviewWithUI, beginInterview, triggerSessionEnd]);

  // ── Keyboard shortcuts ─────────────────────────
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Tab') {
        e.preventDefault();
        toggleEvidenceBoard();
      } else if (e.key === '`') {
        e.preventDefault();
        setEvalStripOn((v) => !v);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [toggleEvidenceBoard]);

  const interviewActive = useGameStore((s) => s.interview.active);
  const activeEvidenceDossier = useGameStore((s) => s.activeEvidenceDossier);
  const hideEvidenceDossier = useGameStore((s) => s.hideEvidenceDossier);
  // BEGIN/END INTV only belongs where there is an interviewable NPC.
  const canInterview = !!getScene(sceneId)?.interview;

  // Closing the EVD FOUND card releases the held Craine reaction (and the
  // instrument readout) for the capture that opened it — the player reads the
  // record first, Craine weighs in after.
  const handleDossierClose = useCallback(() => {
    hideEvidenceDossier();
    const pending = pendingEvidenceReactionRef.current;
    if (!pending) return;
    pendingEvidenceReactionRef.current = null;
    if (pending.description) {
      setTimeout(() => {
        addDialogueLine({ text: pending.description, type: 'npc', speaker: 'CRAINE' });
      }, 450);
    }
    if (pending.lumenReadout) {
      // Panel display, not a transcript line: Craine's read owns the
      // transcript beat, the instrument block lands beside it.
      setTimeout(() => {
        useGameStore.getState().showReadout(pending.lumenReadout);
      }, pending.description ? 1400 : 450);
    }
  }, [hideEvidenceDossier, addDialogueLine]);

  // Lifts the black transition cover once the destination scene has a paintable
  // picture (poster still or first video frame). Stable identity so the
  // CinematicStage ready effect doesn't re-fire every render.
  const handleSceneReady = useCallback(() => {
    useGameStore.getState().markSceneReady();
  }, []);

  useEffect(() => {
    AudioSystem.setRoomTone(sceneId, { carMode, interviewActive });
  }, [sceneId, carMode, interviewActive]);

  useEffect(() => {
    if (!isBooting) {
      try { AudioSystem.start(); } catch (_) {}
      const s = useGameStore.getState();
      AudioSystem.setRoomTone(s.sceneId, { carMode: s.carMode, interviewActive: s.interview.active });
    }
  }, [isBooting]);

  useEffect(() => {
    if (demoStage === 'end') {
      AudioSystem.setRoomTone(null);
      AudioSystem.playOuttro();
    }
    return () => { if (demoStage === 'end') AudioSystem.stopOuttro(); };
  }, [demoStage]);

  // Transition audio:
  // - Heartbeat, pre-bleed, and sweep are handled by BootScreen during establishing
  // - Up arrival: the ding fires from handleElevatorComplete (end of the animated
  //   elevator_up cutscene).
  // - Down arrival: the descent has no cutscene, so the ding fires here when the
  //   lobby paints after the extended black hold (armed in handleNavigation).
  useEffect(() => {
    if (pendingLobbyDingRef.current && sceneId === 'ds_helios_lobby' && sceneReady) {
      pendingLobbyDingRef.current = false;
      try { AudioSystem.playElevatorDing(); } catch (_) {}
    }
  }, [sceneId, sceneReady]);

  // Fire Craine's live briefing once, when the recalibration drops us into the
  // car. Replaces the old hardcoded title-card seed: Craine opens the briefing
  // via the Phase-B model (mocks locally, real Pro on the deployed domain).
  const briefingOpened = useRef(false);
  useEffect(() => {
    if (briefingOpened.current) return;
    if (demoStage === 'playing' && sceneId === 'ds_craine_car' && carMode === 'briefing') {
      briefingOpened.current = true;
      const st = useGameStore.getState();
      // Chip opens the case notebook on the three threads Craine briefs.
      // Day-5 knowledge only; the briefing is the in-fiction source.
      st.seedNotebookFromBriefing();
      st.addDialogueLine({
        text: "Feels like you're back. I gotta say, Chip, I might be getting used to your constant chattering. It's pretty quiet when you go offline like that. Did you lose your memory again? Want me to run you through the case?",
        type: 'npc',
        speaker: 'CRAINE',
      });
      st.appendConversationHistory('craine', {
        role: 'model',
        parts: [{ text: "Feels like you're back. I gotta say, Chip, I might be getting used to your constant chattering. It's pretty quiet when you go offline like that. Did you lose your memory again? Want me to run you through the case?" }]
      });
      seedOpeningLine('craine', "Feels like you're back. I gotta say, Chip, I might be getting used to your constant chattering. It's pretty quiet when you go offline like that. Did you lose your memory again? Want me to run you through the case?");
    }
  }, [demoStage, sceneId, carMode]);

  // When the slice ends (End Card), re-arm the one-shot briefing intro and the
  // session-cap latch so a "Replay slice" drops the player back into the car
  // with Craine's briefing firing again — not a silent, already-opened car.
  useEffect(() => {
    if (demoStage === 'end') {
      briefingOpened.current = false;
      setSessionCapped(false);
    }
  }, [demoStage]);

  const showConclude = demoStage === 'playing' && sceneId === 'ds_craine_car' && carMode === 'debrief';

  // AUDIO control as an in-frame `.glass` panel (rendered inside `.sc` via
  // HudSceneZone's overlay slot, so it is bounded by the frame and blocks the
  // scene — consistent with NAV/EVD, not a full-screen takeover).
  const audioPanel = showSettings ? (
    <div className="glass">
      <div className="glass-t">[ AUDIO ] CONTROL<button className="glass-x" onClick={() => { setShowSettings(false); try { AudioSystem.playNavigation(); } catch (_) {} }}>[ESC]</button></div>
      <div className="audio-settings-row">
        <span>MASTER MUTE:</span>
        <button
          className={`audio-toggle-btn ${muted ? 'muted' : ''}`}
          onClick={() => {
            const newMuted = !muted;
            setMuted(newMuted);
            try { AudioSystem.start(); AudioSystem.setMasterVolume(newMuted ? 0 : masterVol); AudioSystem.playNavigation(); } catch (_) {}
          }}
        >
          {muted ? 'MUTED' : 'ACTIVE'}
        </button>
      </div>
      <div className="audio-slider-group">
        <div className="audio-slider-label"><span>MASTER VOL:</span><span>{Math.round(masterVol * 100)}%</span></div>
        <input type="range" min="0" max="1" step="0.05" value={masterVol}
          onChange={(e) => { const val = parseFloat(e.target.value); setMasterVol(val); try { AudioSystem.start(); if (!muted) AudioSystem.setMasterVolume(val); } catch (_) {} }} />
      </div>
      <div className="audio-slider-group sfx">
        <div className="audio-slider-label"><span>SFX VOL:</span><span>{Math.round(sfxVol * 100)}%</span></div>
        <input type="range" min="0" max="1" step="0.05" value={sfxVol}
          onChange={(e) => { const val = parseFloat(e.target.value); setSfxVol(val); try { AudioSystem.start(); AudioSystem.setSFXVolume(val); } catch (_) {} }} />
      </div>
      <div className="audio-slider-group soundscapes">
        <div className="audio-slider-label"><span>SOUNDSCAPES:</span><span>{Math.round(soundscapeVol * 100)}%</span></div>
        <input type="range" min="0" max="1" step="0.05" value={soundscapeVol}
          onChange={(e) => { const val = parseFloat(e.target.value); setSoundscapeVol(val); try { AudioSystem.start(); AudioSystem.setSoundscapeVolume(val); } catch (_) {} }} />
      </div>
    </div>
  ) : null;

  return (
    <>
      <BootScreen />

      {chapterLoaded && (
      <div className="hud ds-scanlines" onClick={() => { try { AudioSystem.start(); } catch (_) {} }}>
        {evalStripOn && <HudEvalStrip onRunProbe={handleTerminalInput} />}
        <div className="hud-mid">
          <div className="hud-left-col">
            <HudLeftPanel
              scanMode={scanMode}
              onToggleScan={() => setScanMode((v) => !v)}
              onBioscanClick={handleBioscanClick}
              dossierId={activeEvidenceDossier}
              onDossierClose={handleDossierClose}
              onOpenLocker={() => { if (!useGameStore.getState().isEvidenceBoardOpen) toggleEvidenceBoard(); }}
            />
            <HudButtonBar
              interviewActive={interviewActive}
              canInterview={canInterview}
              onToggleInterview={() => { try { AudioSystem.start(); } catch (_) {} return interviewActive ? concludeInterview() : beginInterview(); }}
              onNav={handleNavigation}
              onEvidence={() => { try { AudioSystem.start(); AudioSystem.playNavigation(); } catch (_) {} toggleEvidenceBoard(); }}
              onAudio={() => { setShowSettings(p => !p); try { AudioSystem.start(); AudioSystem.playNavigation(); } catch (_) {} }}
              onEvals={() => setEvalStripOn((v) => !v)}
              evalsOpen={evalStripOn}
              onExit={() => { try { AudioSystem.start(); AudioSystem.playNavigation(); } catch (_) {} setShowExitConfirm(true); }}
            />
          </div>
          <div className="hud-right-col">
            <HudSceneZone sceneId={sceneId} scanMode={scanMode} onHotspotInteract={handleHotspotInteract} onNavigate={handleNavigation} onElevatorComplete={handleElevatorComplete} onBioscanClick={handleBioscanClick} onSceneReady={handleSceneReady} overlay={<>
              {audioPanel}
              <InterviewStage />
              {scanFx && (
                <div className="scene-scanfx" aria-hidden>
                  {scanFx === 'sweep' && <div className="scanfx-line" />}
                  {scanFx === 'done' && <div className="scanfx-chip">■ SCAN COMPLETE — CAPTURED TO RECORD</div>}
                </div>
              )}
            </>} />
            {/* In-frame evidence locker (.glass-wide overlays the right column,
                bounded by the frame and blocking, instead of a full-screen takeover). */}
            <EvidenceBoard />
            <HudTerminal onSubmit={handleTerminalInput} disabled={processing} interviewActive={interviewActive} />
          </div>
        </div>
      </div>
      )}

      {showConclude && (
        <button className="conclude-slice-btn" onClick={() => useGameStore.getState().setDemoStage('end')}>
          Conclude the night ▸
        </button>
      )}

      <div className={`scene-transition ${isTransitioning ? 'active' : ''}`} />
      <div className="crt-overlay" />

      <EndCard />

      {showExitConfirm && (
        <div className="exit-confirm" role="dialog" aria-modal="true" aria-labelledby="exit-confirm-title">
          <div className="exit-confirm-scrim" onClick={() => setShowExitConfirm(false)} />
          <div className="exit-confirm-box">
            <div className="exit-confirm-kicker">END SESSION</div>
            <h2 id="exit-confirm-title" className="exit-confirm-title">Quit the night?</h2>
            <p className="exit-confirm-body">
              This ends the playable slice and takes you to the debrief summary. Any unsaved
              progress in this session won&apos;t carry over.
            </p>
            <div className="exit-confirm-actions">
              <button
                className="exit-confirm-btn cancel"
                onClick={() => { try { AudioSystem.playNavigation(); } catch (_) {} setShowExitConfirm(false); }}
                autoFocus
              >
                Keep playing
              </button>
              <button
                className="exit-confirm-btn quit"
                onClick={() => { setShowExitConfirm(false); useGameStore.getState().setDemoStage('end'); }}
              >
                Quit to debrief ▸
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
